"use client";

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AnyRealtimeMessage } from "@/features/realtime/types";
import {
  findChainWorkIndex,
  readChainWorkBeat,
  readEventChainRef,
  type ChainWorkMode,
} from "@/features/finance/chain-settlement";

/**
 * `chain-work` is the five marketplace actions — list, cancel listing, place,
 * cancel and accept an offer. They are ONE kind and not five, because they
 * differ only in which pair of socket keys they answer to, and that difference
 * is already written down once in `CHAIN_WORK`. The mode rides on the card.
 */
export type ProcessKind = "nft" | "collection" | "drop-mint" | "chain-work";
export type ProcessStatus = "active" | "done" | "failed" | "partial" | "uncertain";

/** Ordered stages the nft/collection flows share. `step` is how many are complete. */
export const TOTAL_STEPS = 3;

/**
 * A drop mint's record.
 *
 * This replaces the old `PurchaseProgress`, which carried `requested` /
 * `acquired` / `charged` counters for a multi-unit purchase sequence. There is
 * no such sequence: `drop-item/mint/{dropItemId}` sends one item id, so there
 * is exactly one request, no quantity, and no amount this client can honestly
 * report as charged. What is left is what is actually knowable — which item,
 * and whether the one request resolved.
 */
export type MintProgress = {
  /** Item name, for a card the user reads after navigating away. */
  itemName: string;
  /** A localised sentence stating the outcome. Caller-supplied. */
  note?: string;
};

/**
 * A marketplace action's chain leg, as the dock follows it.
 *
 * `workId` is the `listing_id` / `offer_id` this attempt is about. It may be
 * unknown at the start — not every POST names the row it created — in which
 * case the first event that finds the card supplies it. After that the id is
 * what keeps two simultaneous offers from closing each other's card.
 */
export type ChainWorkProgress = {
  mode: ChainWorkMode;
  workId?: number | string | null;
  nftId?: number | string | null;
  /** `settleAuction` only: the settlement failed but the auction stayed `settling` — the payment is stuck. */
  stuck?: boolean;
  /** `swap` only: the failed swap was refunded (the event carried `refund_tx`). */
  refunded?: boolean;
};

export type TrackedProcess = {
  id: string;
  kind: ProcessKind;
  title: string;
  image?: string;
  /** Server id, known only once the create_* event lands. */
  entityId?: number;
  txHash?: string;
  /** 1 = submitted, 2 = created server-side, 3 = written on-chain. Unused by mints. */
  step: number;
  status: ProcessStatus;
  /** Set when status is "failed" — shown verbatim to the user. */
  error?: string;
  /** Present only on `kind: "drop-mint"`. */
  mint?: MintProgress;
  /** Present only on `kind: "chain-work"`. */
  chain?: ChainWorkProgress;
  startedAt: number;
  updatedAt: number;
};

/** Nothing useful can still arrive after this; a process left hanging this long
 *  is a dead entry we refuse to keep in memory or on screen. */
export const STALE_AFTER_MS = 15 * 60 * 1000;
/**
 * How long a card keeps SAYING it is watching the chain before it admits it
 * has stopped hearing anything.
 *
 * The on-chain step has exactly one witness — a socket message on the user's
 * own channel — and there is no endpoint to ask instead: a collection is
 * fetched by contract address and an NFT by contract + token id, and neither
 * exists yet while the thing is being written. So when that message does not
 * come, the client genuinely does not know, and the only question is how long
 * it should imply otherwise.
 *
 * Four minutes is well past the ordinary case and well short of the fifteen
 * that `STALE_AFTER_MS` allows before an entry is treated as dead memory.
 * Those two are different questions and now have different answers: this one
 * is about what the card is allowed to claim, not about when to free it.
 */
export const CHAIN_QUIET_AFTER_MS = 4 * 60 * 1000;
/** An AI draft's metadata: `ai_nft_metadata_failed` comes after about five minutes, so the card outwaits it. */
export const AI_QUIET_AFTER_MS = 8 * 60 * 1000;
/** Hard cap so a pathological event stream can't grow the list without bound. */
const MAX_TRACKED = 6;
/** How long a finished card lingers before it clears itself. */
export const DONE_LINGER_MS = 12_000;

type ProcessState = {
  processes: TrackedProcess[];
  start: (input: { kind: ProcessKind; title: string; image?: string }) => string;
  /**
   * Opens a card for one of the five marketplace chain actions. Returns its id.
   *
   * Called the moment the POST comes back queued, which is also the moment the
   * dialog closes — the card is what carries the rest, across pages.
   */
  startChainWork: (input: {
    mode: ChainWorkMode;
    title: string;
    image?: string;
    workId?: number | string | null;
    nftId?: number | string | null;
  }) => string;
  /** Terminal success for a chain card, for a settlement seen off the socket. */
  settleChainWork: (id: string) => void;
  /**
   * THE REQUEST CAME BACK. Step one is over — and this is the thing the
   * create flows never told their own card.
   *
   * `create_nft` / `create_collection` on the socket said the same thing, so
   * the card was left waiting for a second source to confirm what the caller
   * was already holding in its hand. When that message did not arrive the
   * card sat on “Submitting details…” at 33% — for an NFT that had been
   * created — until the sweep quietly removed it.
   *
   * `entityId` is bound here when the response named one, which is what lets
   * the on-chain event afterwards find THIS card by id instead of guessing at
   * the oldest unbound one.
   */
  submitted: (id: string, entityId?: number | string | null) => void;
  applyEvent: (message: AnyRealtimeMessage) => void;
  /** Opens a mint card. Returns its id, which the attempt then settles. */
  startMint: (input: { title: string; image?: string; itemName: string }) => string;
  /**
   * Terminal state for a mint. There is no "partial" here — one request either
   * resolved, was refused, or got no answer at all (`uncertain`).
   */
  settleMint: (
    id: string,
    input: {
      status: Extract<ProcessStatus, "done" | "failed" | "uncertain">;
      note?: string;
      error?: string;
    },
  ) => void;
  fail: (id: string, error?: string) => void;
  dismiss: (id: string) => void;
  clearFinished: () => void;
  /** Drops finished cards past their linger window and stale active ones. */
  sweep: () => void;
};

const now = () => Date.now();

function newId(): string {
  return `p_${now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Is this card still waiting for the chain to report?
 *
 * `uncertain` COUNTS, and leaving it out was a hole in the very thing that
 * state exists for. It means the card stopped hearing — not that it stopped
 * caring — and the event it gave up on is precisely the one that may still
 * arrive, late, after a reconnect or a backgrounded tab. Refusing it there
 * would leave the card frozen on “we do not know” while the answer was on the
 * wire.
 */
function isOpen(p: TrackedProcess) {
  return p.status === "active" || p.status === "uncertain";
}

/** Oldest still-waiting process of a kind that hasn't been bound to a server id. */
function findUnbound(list: TrackedProcess[], kind: ProcessKind) {
  return list.find((p) => p.kind === kind && isOpen(p) && p.entityId == null);
}

function findByEntity(list: TrackedProcess[], kind: ProcessKind, entityId: number) {
  return list.find((p) => p.kind === kind && p.entityId === entityId);
}

export const useProcessStore = create<ProcessState>()(
  persist(
    (set, get) => ({
      processes: [],

      start: ({ kind, title, image }) => {
        const id = newId();
        const entry: TrackedProcess = {
          id,
          kind,
          title,
          image,
          step: 1,
          status: "active",
          startedAt: now(),
          updatedAt: now(),
        };
        set((state) => ({ processes: [entry, ...state.processes].slice(0, MAX_TRACKED) }));
        return id;
      },

      startChainWork: ({ mode, title, image, workId, nftId }) => {
        const id = newId();
        const entry: TrackedProcess = {
          id,
          kind: "chain-work",
          title,
          image,
          // 1 = the request is in. The two socket beats supply 2 and 3.
          step: 1,
          status: "active",
          chain: { mode, workId: workId ?? null, nftId: nftId ?? null },
          startedAt: now(),
          updatedAt: now(),
        };
        set((state) => ({ processes: [entry, ...state.processes].slice(0, MAX_TRACKED) }));
        return id;
      },

      settleChainWork: (id) =>
        set((state) => ({
          processes: state.processes.map((p) =>
            p.id === id && p.chain && isOpen(p)
              ? { ...p, step: TOTAL_STEPS, status: "done", updatedAt: now() }
              : p,
          ),
        })),

      startMint: ({ title, image, itemName }) => {
        const id = newId();
        const entry: TrackedProcess = {
          id,
          kind: "drop-mint",
          title,
          image,
          step: 0,
          status: "active",
          mint: { itemName },
          startedAt: now(),
          updatedAt: now(),
        };
        set((state) => ({ processes: [entry, ...state.processes].slice(0, MAX_TRACKED) }));
        return id;
      },

      settleMint: (id, { status, note, error }) =>
        set((state) => ({
          processes: state.processes.map((p) =>
            p.id === id && p.mint
              ? { ...p, status, mint: { ...p.mint, note }, error, updatedAt: now() }
              : p,
          ),
        })),

      submitted: (id, entityId) =>
        set((state) => ({
          processes: state.processes.map((p) => {
            if (p.id !== id || !isOpen(p)) return p;
            const bound = Number(entityId);
            return {
              ...p,
              // Never LOWERS a step. The socket can legitimately beat the
              // response back — the chain leg starts server-side the moment
              // the row exists — and a card already on 3 must not drop to 2.
              step: Math.max(p.step, 2),
              entityId:
                p.entityId ?? (Number.isFinite(bound) ? bound : undefined),
              updatedAt: now(),
            };
          }),
        })),

      applyEvent: (message) => {
        const key = message.key;
        const data = (message.data ?? {}) as Record<string, any>;

        set((state) => {
          const list = state.processes;

          const patch = (target: TrackedProcess | undefined, next: Partial<TrackedProcess>) => {
            if (!target) return state;
            return {
              processes: list.map((p) =>
                p.id === target.id ? { ...p, ...next, updatedAt: now() } : p,
              ),
            };
          };

          /** The create pair's shared rule — see the cases below. */
          const bindCreated = (kind: ProcessKind, rawId: unknown) => {
            const target =
              findUnbound(list, kind) ?? findByEntity(list, kind, Number(rawId));
            if (!target || !isOpen(target)) return state;
            const bound = Number(rawId);
            return patch(target, {
              entityId: Number.isFinite(bound) ? bound : target.entityId,
              step: Math.max(target.step, 2),
            });
          };

          // THE FIVE MARKETPLACE ACTIONS, FIRST — they are keyed off the table
          // in `chain-settlement.ts` rather than spelled out again here, so a
          // sixth row there needs no edit in this file. Every other key falls
          // through to the switch below, unchanged.
          const beat = readChainWorkBeat(key);
          if (beat) {
            const event = readEventChainRef(beat.mode, data);
            const index = findChainWorkIndex(
              list.map((p) => ({
                mode: p.chain?.mode ?? beat.mode,
                id: p.chain?.workId ?? null,
                nftId: p.chain?.nftId ?? null,
                // A card of another kind can never be a candidate: `chain` is
                // what makes one, and `open` is false without it.
                open: !!p.chain && isOpen(p),
              })),
              beat.mode,
              event,
            );
            if (index < 0) return state;
            const target = list[index];
            return patch(target, {
              chain: {
                ...target.chain!,
                // The event's id is adopted only to FILL a blank. A card that
                // already names one was matched on it and cannot be renamed.
                workId: target.chain?.workId ?? event.id ?? null,
                nftId: target.chain?.nftId ?? event.nftId ?? null,
              },
              ...(beat.beat === "settled"
                ? { step: TOTAL_STEPS, status: "done" as const }
                : // The in-flight beat is NOT a result — it only says the chain
                  // leg has started. Never lowers a step it has already passed.
                  { step: Math.max(target.step, 2) }),
            });
          }

          switch (key) {
            // Server acknowledged the entity — bind the local card to its id.
            //
            // IT CAN ARRIVE AFTER THE ON-CHAIN EVENT IT PRECEDES. Nothing orders
            // these two, and `findByEntity` does not care whether a card is
            // finished, so a late `create_nft` used to drag a card that was
            // already `done` at 3/3 back down to 2/3 — a green tick over a
            // two-thirds bar, for an NFT that was fully minted. `bindCreated`
            // only ever raises the step, and only on a card still waiting.
            case "create_nft":
              return bindCreated("nft", data.nft_id);

            case "create_collection":
              return bindCreated("collection", data.collection_id);

            // Written on-chain — this is the terminal success signal.
            case "store_nft_on_blockchain":
              return patch(
                findByEntity(list, "nft", Number(data.nft_id)) ?? findUnbound(list, "nft"),
                {
                  entityId: Number(data.nft_id),
                  txHash: data.transaction_hash,
                  step: TOTAL_STEPS,
                  status: "done",
                },
              );

            case "store_collection_on_blockchain":
              return patch(
                findByEntity(list, "collection", Number(data.collection_id)) ??
                  findUnbound(list, "collection"),
                {
                  entityId: Number(data.collection_id),
                  txHash: data.transaction_hash,
                  step: TOTAL_STEPS,
                  status: "done",
                },
              );

            // push_to_blockchain / fund_transaction carry a job/transaction id
            // that maps to nothing we hold, so they cannot advance a card —
            // guessing which one they belong to would show the wrong NFT as
            // finished.
            //
            // THEY NO LONGER TOUCH THE ACTIVITY CLOCK EITHER, and that was not
            // harmless. `patch` stamps `updatedAt`, so an arbitrary active card
            // — whichever happened to be first in the list — had its silence
            // timer reset by traffic that said nothing about it. A card that was
            // never going to hear its own event could be kept 'fresh' forever by
            // somebody else's, which is how a stuck card outlived every window
            // meant to end it.
            // An auction's settlement failed — the one chain-work row with a failure event. Its card
            // (matched on the auction id, else the only open settlement) states the backend's reason;
            // a payment left `settling` is said as stuck, so the winner is not chosen a second time.
            case "auction_settle_failed": {
              const auctionId = data.auction_id ?? data.auction?.id ?? null;
              const open = list.filter((p) => p.chain?.mode === "settleAuction" && isOpen(p));
              const target = open.find((p) => auctionId != null && String(p.chain?.workId) === String(auctionId))
                ?? (open.length === 1 && (auctionId == null || open[0].chain?.workId == null) ? open[0] : undefined);
              if (!target) return state;
              const reason = typeof data.message === "string" && data.message.trim() ? data.message.trim() : undefined;
              // The dock words it in the page's language (ProcessTracker); the store keeps the facts.
              return patch(target, { status: "failed", error: reason, chain: { ...target.chain!, stuck: data.status === "settling" } });
            }

            // A SWAP's two beats the table does not carry: its registration names the card's swap id
            // (a card opened before the POST returned has none yet), and its failure ends it.
            // One swap at a time, so the only open swap card is the one an id-less event means.
            case "create_swap":
            case "swap_failed": {
              const swapId = data.swap_id ?? null;
              const open = list.filter((p) => p.chain?.mode === "swap" && isOpen(p));
              const target = open.find((p) => swapId != null && String(p.chain?.workId) === String(swapId))
                ?? open.find((p) => p.chain?.workId == null);
              if (!target) return state;
              const chain = { ...target.chain!, workId: target.chain?.workId ?? swapId };
              if (key === "create_swap") return patch(target, { chain });
              return patch(target, { status: "failed", chain: { ...chain, refunded: Boolean(data.refund_tx) } });
            }

            // An AI draft's metadata never came (about five minutes): its card — matched on the draft,
            // else the one open AI card without one — fails with the backend's reason.
            case "ai_nft_metadata_failed": {
              const nftId = data.nft_id ?? null;
              const open = list.filter((p) => p.chain?.mode === "aiMetadata" && isOpen(p));
              const target = open.find((p) => nftId != null && String(p.chain?.workId) === String(nftId))
                ?? open.find((p) => p.chain?.workId == null);
              if (!target) return state;
              const reason = typeof data.error === "string" && data.error.trim() ? data.error.trim() : undefined;
              return patch(target, { status: "failed", error: reason, chain: { ...target.chain!, workId: target.chain?.workId ?? nftId } });
            }

            case "push_to_blockchain":
            case "fund_transaction":
              return state;

            default:
              return state;
          }
        });
      },

      fail: (id, error) =>
        set((state) => ({
          processes: state.processes.map((p) =>
            p.id === id ? { ...p, status: "failed", error, updatedAt: now() } : p,
          ),
        })),

      dismiss: (id) =>
        set((state) => ({ processes: state.processes.filter((p) => p.id !== id) })),

      clearFinished: () =>
        set((state) => ({ processes: state.processes.filter((p) => p.status === "active") })),

      sweep: () => {
        const t = now();
        const current = get().processes;
        let changed = false;

        const next = current.flatMap<TrackedProcess>((p) => {
          // A settled DROP MINT is the user's record that an irreversible
          // request left this browser, so it does not linger-and-vanish the way
          // a create-nft card does — it stays until dismissed. That is what
          // makes an interrupted mint still readable after the user has
          // navigated away from the drop.
          if (p.kind === "drop-mint") {
            if (p.status !== "active") return [p];
            if (t - p.updatedAt < STALE_AFTER_MS) return [p];
            // A mint that stopped reporting is not "finished" and is not
            // "failed" — we do not know whether it went through. Downgrading it
            // to uncertain keeps that honest instead of deleting the card and
            // implying nothing happened.
            changed = true;
            return [{ ...p, status: "uncertain" as const, updatedAt: t }];
          }

          // AN ACTIVE CARD THAT HAS GONE QUIET IS NOT DELETED, it is downgraded
          // — the same answer a drop mint already gets, for the same reason. It
          // used to be removed silently, so a user who created an NFT and
          // watched the card spin was left with no card and no statement either
          // way. `uncertain` says the one true thing: we stopped hearing, go and
          // look. It is never `failed`, which would invite a second create of
          // something that very probably already exists.
          // An AI draft's card waits longer: the backend itself gives up after about five minutes, and says so.
          const quietAfter = p.chain?.mode === "aiMetadata" ? AI_QUIET_AFTER_MS : CHAIN_QUIET_AFTER_MS;
          if (p.status === "active" && t - p.updatedAt >= quietAfter) {
            changed = true;
            return [{ ...p, status: "uncertain" as const, updatedAt: t }];
          }

          // `uncertain` outlives the linger window on purpose: it is a statement
          // the user has to read, not an acknowledgement that fades.
          if (p.status === "uncertain") return [p];

          const keep = p.status === "active" || t - p.updatedAt < DONE_LINGER_MS;
          if (!keep) changed = true;
          return keep ? [p] : [];
        });

        if (changed) set({ processes: next });
      },
    }),
    {
      name: "nftbaz-processes",
      // sessionStorage, not localStorage: an in-flight mint belongs to this tab
      // and this session. It survives a reload or a hard navigation without
      // leaving stale cards behind in every future visit.
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ processes: state.processes }),
      onRehydrateStorage: () => (state) => {
        // Anything restored from a previous page load is already old; drop what
        // can no longer make progress rather than showing a frozen spinner.
        state?.sweep();
      },
    },
  ),
);
